/**
 * Stop Echo from re-authorising private channels the server keeps refusing.
 *
 * pusher-js re-subscribes every held channel on each reconnect and Echo never gives up,
 * so a tab whose session died hammers /app/broadcasting/auth forever. After
 * MAX_REFUSALS refusals we leave the channel, which takes it out of the reconnect set.
 * Only 401/403 count: those are verdicts on the credentials. Transport errors, 419,
 * 429 and 5xx are transient, keep retrying. A successful subscription resets the count.
 */

const MAX_REFUSALS = 5;

export default function guardEchoChannelAuth(echo) {
  const refusals = new Map();
  const watched = new WeakSet();
  const subscribe = echo.private.bind(echo);

  // eslint-disable-next-line no-param-reassign
  echo.private = (name) => {
    const channel = subscribe(name);
    const fullName = `private-${name}`;

    // Echo hands out one channel object per name, so only bind our handlers once.
    if (watched.has(channel)) {
      return channel;
    }
    watched.add(channel);

    channel.subscribed(() => refusals.delete(fullName));

    channel.error((status) => {
      if (!status || (status.status !== 401 && status.status !== 403)) {
        return;
      }

      const count = (refusals.get(fullName) || 0) + 1;
      refusals.set(fullName, count);

      if (count >= MAX_REFUSALS) {
        echo.leaveChannel(fullName);
        console.warn(`Echo: left "${fullName}" after ${count} refused channel authorisations (${status.status}).`);
      }
    });

    return channel;
  };

  return echo;
}
